import { useEffect, useState } from "react";
import { luxuryServiceVisions } from "@/config/luxury-visions";

const STORAGE_KEY = "revolin.luxury-vision.last";

function visionCandidates(rotationKey: string) {
  const key = rotationKey.toLowerCase();
  const systems = key.includes("metal")
    ? ["metal", "mixed"]
    : /flat|low-slope|membrane|sbs|tpo|pvc/.test(key)
      ? ["flat", "mixed"]
      : key.includes("cedar")
        ? ["cedar", "mixed"]
        : /shingle|asphalt/.test(key)
          ? ["shingle"]
          : null;

  return systems
    ? luxuryServiceVisions.filter((image) => systems.includes(image.roofSystem))
    : luxuryServiceVisions;
}

function stableIndex(key: string, length: number): number {
  let hash = 0;
  for (let index = 0; index < key.length; index += 1) {
    hash = (hash * 31 + key.charCodeAt(index)) >>> 0;
  }
  return hash % length;
}

function randomIndex(length: number): number {
  if (typeof window !== "undefined" && window.crypto?.getRandomValues) {
    const value = new Uint32Array(1);
    window.crypto.getRandomValues(value);
    return value[0]! % length;
  }
  return Math.floor(Math.random() * length);
}

export function LuxuryVisionBackdrop({
  rotationKey,
  priority = false,
}: {
  rotationKey: string;
  priority?: boolean;
}) {
  const candidates = visionCandidates(rotationKey);
  const [image, setImage] = useState(
    () => candidates[stableIndex(rotationKey, candidates.length)]!,
  );

  useEffect(() => {
    let active = true;
    let previousId = image.id;

    try {
      previousId = window.sessionStorage.getItem(STORAGE_KEY) ?? previousId;
    } catch {
      // Storage can be unavailable in private browsing or hardened environments.
    }

    const available = candidates.filter((candidate) => candidate.id !== previousId);
    const next = available[randomIndex(available.length)] ?? candidates[0]!;
    const preload = new Image();
    preload.src = next.src;
    preload.onload = () => {
      if (!active) return;
      setImage(next);
      try {
        window.sessionStorage.setItem(STORAGE_KEY, next.id);
      } catch {
        // The rotation still works for this page even when storage is unavailable.
      }
    };

    return () => {
      active = false;
    };
    // A new route key intentionally advances the image once per page visit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rotationKey]);

  return (
    <>
      <img
        src={image.src}
        alt=""
        width={image.width}
        height={image.height}
        fetchPriority={priority ? "high" : undefined}
        loading={priority ? "eager" : "lazy"}
        className="absolute inset-0 -z-20 size-full object-cover transition-opacity duration-700"
      />
    </>
  );
}
